import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { StyleSheet, View, ActivityIndicator, RefreshControl, ScrollView } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import SettingsSection from '@/components/SettingsSection';
import { useToastStore, useAuthStore, useInferenceTasksStore, useModalStore } from '@/store';
import InferenceTaskListItem from '@/components/InferenceTaskListItem';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyState: {
    textAlign: 'center',
    paddingVertical: 40,
  },
  tasksList: {
    width: '100%',
    marginTop: 10,
    gap: 8,
  },
  createSection: {
    marginBottom: 16,
  },
  divider: {
    borderColor: colors.border_color_secondary,
    borderWidth: 0.5,
    marginTop: 5,
    marginBottom: 20,
  },
});

export default function TasksSettings() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  // Tasks store - state selectors
  const tasksMap = useInferenceTasksStore(state => state.tasks);
  const taskIds = useInferenceTasksStore(state => state.taskIds);
  const isLoading = useInferenceTasksStore(state => state.isLoading.fetch);
  const errors = useInferenceTasksStore(state => state.errors);

  // Tasks store - actions
  const fetchTasks = useInferenceTasksStore(state => state.fetchTasks);
  const deleteTask = useInferenceTasksStore(state => state.deleteTask);
  const pagination = useInferenceTasksStore(state => state.pagination);

  // Derive tasks list from map and ids using useMemo
  const tasks = useMemo(() => {
    return taskIds.map(id => tasksMap[id]).filter(Boolean);
  }, [tasksMap, taskIds]);

  const styles = useMemo(() => createStyles(colors), [colors]);

  useEffect(() => {
    fetchTasks(1).catch((err) => {
      console.error('Error fetching tasks:', err);
    });

  }, [fetchTasks]);

  const handleCreateTask = useCallback(() => {
    if (isGhost) {
      openModal('GhostSignupModal');
      return;
    }
    openModal('CreateTaskModal');
  }, [openModal, isGhost]);

  const handleTaskPress = useCallback((task) => {
    openModal('CreateTaskModal', {
      task,
      onSuccess: (result) => {
        if (result?.action === 'delete' && result?.taskId) {
          showConfirmationModal({
            title: t('settings.tasks.deleteTask'),
            message: t('settings.tasks.deleteTaskConfirm'),
            confirmText: t('common.delete'),
            isDangerous: true,
            onConfirm: async () => {
              try {
                await deleteTask(result.taskId);
                addToast(t('settings.tasks.deleteTaskSuccess'), 'success');
              } catch (error) {
                console.error('Failed to delete task:', error);
                addToast(t('settings.tasks.deleteTaskFailed'), 'error');
              }
            }
          });
        }
      }
    });
  }, [openModal, showConfirmationModal, deleteTask, addToast]);

  if (isLoading && !tasks.length) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.text_color_primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SettingsSection
        title={t('settings.tasks.createTask')}
        subtitle={t('settings.tasks.createTaskSubtitle')}
        isLast
        isColumn
      >
        <View style={styles.createSection}>
          <Button
            text={t('settings.tasks.createNewTask')}
            variant="secondary"
            prependIcon="plus"
            onPress={handleCreateTask}
          />
        </View>
      </SettingsSection>

      <View style={styles.divider} />

      <SettingsSection
        title={t('settings.tasks.yourTasks')}
        subtitle={tasks.length > 0 ? t('settings.tasks.tasksConfigured').replace('%COUNT%', tasks.length) : t('settings.tasks.noTasksYet')}
        isBold
        isColumn
        isLast
      >
        {errors.fetch && !tasks.length ? (<View style={{ gap: 12 }}>
          <Text size="n" color="danger" style={styles.emptyState}>
            {t('settings.tasks.errorLoadingTasks')}
          </Text>
          <Button text={t('common.retry')} onPress={() => fetchTasks(1).catch(() => {})} />
          </View>
        ) : tasks.length === 0 ? (
          <Text size="n" color="secondary" style={styles.emptyState}>
            {t('settings.tasks.noTasksCreated')}
          </Text>
        ) : (
          <View style={styles.tasksList}>
            {tasks.map(task => (
              <InferenceTaskListItem
                key={task.id}
                task={task}
                onPress={handleTaskPress}
              />
            ))}
          </View>
        )}
        {!!tasks.length && !!errors.fetch && <Text accessibilityRole="alert" color="danger">{t('settings.tasks.errorLoadingTasks')}</Text>}
        {pagination?.hasMore && <Button text={t('common.loadMore')} isLoading={isLoading} isDisabled={isLoading} onPress={() => fetchTasks(pagination.page + 1).catch(() => {})} />}
      </SettingsSection>
    </View>
  );
}
